/**
 * Form parts for BankAccountsStep — extracted to keep the main file under
 * the line-per-function / complexity limits.
 */

import { useBaseCurrency, useEnabledCurrencies } from '../../hooks/use-currencies'
import { DatePicker, Input, Select } from '../ui'

import type { BankAccountInput } from '../../types/company-setup'

export const BANK_ACCOUNT_TYPES = [
  { value: 'current', label: 'Current Account' },
  { value: 'savings', label: 'Savings Account' },
  { value: 'od', label: 'Overdraft (OD) Account' },
  { value: 'cc', label: 'Cash Credit (CC) Account' },
  { value: 'fd', label: 'Fixed Deposit (FD)' },
  { value: 'cash', label: 'Cash on Hand' },
]

type UpdateFn = (index: number, field: keyof BankAccountInput, value: string | number) => void

interface CommonFieldProps {
  account: BankAccountInput
  index: number
  onUpdate: UpdateFn
}

function CoreFields({ account, index, onUpdate }: CommonFieldProps): JSX.Element {
  const { currencies: CURRENCIES } = useEnabledCurrencies()
  const baseCurrency = useBaseCurrency()
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={`bank-${index}-account-name`}>
          Account Name <span className="text-red-500">*</span>
        </label>
        <Input
          id={`bank-${index}-account-name`}
          placeholder="e.g., HDFC Current Account"
          type="text"
          value={account.name}
          onChange={(e): void => onUpdate(index, 'name', e.target.value)}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={`bank-${index}-account-type`}>
          Account Type
        </label>
        <Select
          hideMessage
          id={`bank-${index}-account-type`}
          options={BANK_ACCOUNT_TYPES.map((type) => ({ value: type.value, label: type.label }))}
          value={account.accountType}
          onChange={(value): void => onUpdate(index, 'accountType', value)}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={`bank-${index}-currency`}>
          Currency <span className="text-red-500">*</span>
        </label>
        <Select
          hideMessage
          id={`bank-${index}-currency`}
          options={CURRENCIES.map((curr) => ({ value: curr.value, label: curr.label }))}
          value={account.currency ?? baseCurrency}
          onChange={(value): void => onUpdate(index, 'currency', value)}
        />
      </div>
    </>
  )
}

interface LabeledInputProps {
  id: string
  label: string
  placeholder: string
  value: string
  onChange: (v: string) => void
}

function LabeledInput({ id, label, placeholder, value, onChange }: LabeledInputProps): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={id}>
        {label}
      </label>
      <Input
        id={id}
        placeholder={placeholder}
        type="text"
        value={value}
        onChange={(e): void => onChange(e.target.value)}
      />
    </div>
  )
}

function BankBranchFields({ account, index, onUpdate }: CommonFieldProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 el-gap-field">
      <LabeledInput
        id={`bank-${index}-bank-name`}
        label="Bank Name"
        placeholder="e.g., HDFC Bank"
        value={account.bankName ?? ''}
        onChange={(v): void => onUpdate(index, 'bankName', v)}
      />
      <LabeledInput
        id={`bank-${index}-branch`}
        label="Branch"
        placeholder="e.g., Mumbai Main"
        value={account.branch ?? ''}
        onChange={(v): void => onUpdate(index, 'branch', v)}
      />
      <LabeledInput
        id={`bank-${index}-account-number`}
        label="Account Number"
        placeholder="Full account number"
        value={account.accountNumber ?? ''}
        onChange={(v): void => onUpdate(index, 'accountNumber', v)}
      />
      <LabeledInput
        id={`bank-${index}-name-at-bank`}
        label="Name at Bank"
        placeholder="Account holder name per bank records"
        value={account.nameAtBank ?? ''}
        onChange={(v): void => onUpdate(index, 'nameAtBank', v)}
      />
    </div>
  )
}

function IfscOrSwift({ account, index, onUpdate }: CommonFieldProps): JSX.Element {
  if (account.currency === 'INR') {
    return (
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={`bank-${index}-ifsc-code`}>
          IFSC Code
        </label>
        <Input
          helperText="Required for Indian bank accounts"
          id={`bank-${index}-ifsc-code`}
          maxLength={11}
          placeholder="e.g., HDFC0001234"
          type="text"
          value={account.ifscCode ?? ''}
          onChange={(e): void => onUpdate(index, 'ifscCode', e.target.value.toUpperCase())}
        />
      </div>
    )
  }
  return (
    <div>
      <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={`bank-${index}-swift-code`}>
        SWIFT/BIC Code
      </label>
      <Input
        helperText="Required for international transfers"
        id={`bank-${index}-swift-code`}
        maxLength={11}
        placeholder="e.g., HDFCINBB"
        type="text"
        value={account.swiftCode ?? ''}
        onChange={(e): void => onUpdate(index, 'swiftCode', e.target.value.toUpperCase())}
      />
    </div>
  )
}

function RoutingNumberField({ account, index, onUpdate }: CommonFieldProps): JSX.Element | null {
  if (account.currency !== 'USD') { return null }
  return (
    <div>
      <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={`bank-${index}-routing-number`}>
        ABA Routing Number
      </label>
      <Input
        helperText="9-digit routing number"
        id={`bank-${index}-routing-number`}
        maxLength={9}
        placeholder="e.g., 021000021"
        type="text"
        value={account.routingNumber ?? ''}
        onChange={(e): void => onUpdate(index, 'routingNumber', e.target.value)}
      />
    </div>
  )
}

function SortCodeField({ account, index, onUpdate }: CommonFieldProps): JSX.Element | null {
  if (account.currency !== 'GBP') { return null }
  return (
    <div>
      <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={`bank-${index}-sort-code`}>
        Sort Code
      </label>
      <Input
        helperText="6-digit sort code (XX-XX-XX)"
        id={`bank-${index}-sort-code`}
        maxLength={8}
        placeholder="e.g., 12-34-56"
        type="text"
        value={account.sortCode ?? ''}
        onChange={(e): void => onUpdate(index, 'sortCode', e.target.value)}
      />
    </div>
  )
}

const IBAN_CURRENCIES = new Set(['EUR', 'GBP', 'AED'])

function IbanField({ account, index, onUpdate }: CommonFieldProps): JSX.Element | null {
  if (!IBAN_CURRENCIES.has(account.currency ?? '')) { return null }
  return (
    <div className="md:col-span-2">
      <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={`bank-${index}-iban`}>
        IBAN
      </label>
      <Input
        helperText="International Bank Account Number (up to 34 characters)"
        id={`bank-${index}-iban`}
        maxLength={34}
        placeholder="e.g., GB29NWBK60161331926819"
        type="text"
        value={account.iban ?? ''}
        onChange={(e): void => onUpdate(index, 'iban', e.target.value.toUpperCase().replace(/\s/g, ''))}
      />
    </div>
  )
}

function IdentifierFields(props: CommonFieldProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 el-gap-field">
      <IfscOrSwift {...props} />
      <RoutingNumberField {...props} />
      <SortCodeField {...props} />
      <IbanField {...props} />
    </div>
  )
}

function OpeningBalanceFields({ account, index, onUpdate }: CommonFieldProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 el-gap-field">
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={`bank-${index}-opening-balance`}>
          Opening Balance
        </label>
        <Input
          id={`bank-${index}-opening-balance`}
          min="0"
          placeholder="0.00"
          step="0.01"
          type="number"
          value={account.openingBalance}
          onChange={(e): void => onUpdate(index, 'openingBalance', parseFloat(e.target.value) || 0)}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={`bank-${index}-opening-date`}>
          As of Date
        </label>
        <DatePicker
          id={`bank-${index}-opening-date`}
          placeholder="Select balance date"
          value={account.openingDate}
          onChange={(date): void => {
            if (date) {
              const year = date.getFullYear()
              const month = String(date.getMonth() + 1).padStart(2, '0')
              const day = String(date.getDate()).padStart(2, '0')
              onUpdate(index, 'openingDate', `${year}-${month}-${day}`)
            } else {
              onUpdate(index, 'openingDate', '')
            }
          }}
        />
      </div>
    </div>
  )
}

interface BankAccountFormProps {
  account: BankAccountInput
  index: number
  onUpdate: UpdateFn
}

export function BankAccountForm({ account, index, onUpdate }: BankAccountFormProps): JSX.Element {
  const common = { account, index, onUpdate }
  return (
    <div className="flex flex-col el-gap-field">
      <CoreFields {...common} />
      <BankBranchFields {...common} />
      <IdentifierFields {...common} />
      <OpeningBalanceFields {...common} />
    </div>
  )
}
